// UI4 theme mode — dark (default) / light. Resolved BEFORE first render so the
// correct token set is active the moment stylesheets evaluate (no flash). The
// light values live in `html[data-theme="light"]` in zap.css; brand green is
// constant across both modes. Persisted in localStorage, else the OS
// preference, else dark.

const KEY = 'zapTheme';

export function resolveTheme() {
  try {
    const saved = localStorage.getItem(KEY);
    if (saved === 'light' || saved === 'dark') return saved;
  } catch { /* storage blocked */ }
  try {
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: light)').matches) {
      return 'light';
    }
  } catch { /* no matchMedia */ }
  return 'dark';
}

// Apply a mode to the document (sets <html data-theme> + the UA color-scheme
// meta so native form controls / scrollbars match). Does NOT persist.
export function applyTheme(mode = resolveTheme()) {
  const m = mode === 'light' ? 'light' : 'dark';
  document.documentElement.setAttribute('data-theme', m);
  let meta = document.querySelector('meta[name="color-scheme"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = 'color-scheme';
    document.head.appendChild(meta);
  }
  meta.content = m;
  return m;
}

export function currentTheme() {
  return document.documentElement.getAttribute('data-theme') === 'light' ? 'light' : 'dark';
}

// Persist + apply.
export function setTheme(mode) {
  const m = mode === 'light' ? 'light' : 'dark';
  try { localStorage.setItem(KEY, m); } catch { /* storage blocked */ }
  return applyTheme(m);
}

export function toggleTheme() {
  return setTheme(currentTheme() === 'light' ? 'dark' : 'light');
}

// The user's stored CHOICE (not the resolved theme): 'dark' | 'light' | 'system'
// ('system' = no stored override, follow prefers-color-scheme).
export function currentChoice() {
  try {
    const s = localStorage.getItem(KEY);
    if (s === 'light' || s === 'dark') return s;
  } catch { /* storage blocked */ }
  return 'system';
}

// Apply a tri-state choice. 'system' clears the override and re-resolves from
// the OS preference; 'dark'/'light' persist an explicit override.
export function setThemeChoice(choice) {
  if (choice === 'system') {
    try { localStorage.removeItem(KEY); } catch { /* storage blocked */ }
    return applyTheme(resolveTheme());
  }
  return setTheme(choice);
}
